import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import FlexBox from 'components/common/FlexBox';
import CitiesTableComponent from './CitiesTableComponent';
import { TableCell } from '@mui/material';
import {
  CitySelectionCheckbox,
  DistrictContainer,
  DistrictRow,
  DistrictsCitiesCard,
  DistrictsCitiesContainer,
  DistrictsContainer
} from '../Styles';
import DistrictComponent from './DistrictComponent';
import { Typography } from '@totalsoft/rocket-ui';
import { makeVar, useReactiveVar } from '@apollo/client';
import { isDistrictDirtyVar, selectedDistrictIdsVar } from 'features/city/variables';
import { isNewEntity, parseId } from 'utils/functions';
import { CityType } from 'features/city/constants';
import useQuery from 'hooks/apollo/useQuery';
import { CHILD_CITIES } from 'features/city/queries';
import { useTranslation } from 'react-i18next';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_CITIES_WRITE } from 'constants/rightsCodes';

const cancelDistrictAddRef = makeVar();

const HierarchyDetailsComponent = ({ id }) => {
  const { t } = useTranslation();

  // #region Data loading

  const { data, loading, refetch } = useQuery(CHILD_CITIES, {
    variables: { parentId: id },
    skip: !id
  });

  const districts = useMemo(() => data?.childCities || [], [data]);

  // #endregion

  const canWrite = usePermission(ADMIN_CITIES_WRITE);

  const [highlightedDistrict, setHighlightedDistrict] = useState(null);

  const selectedDistrictIds = useReactiveVar(selectedDistrictIdsVar);
  const isDistrictDirty = useReactiveVar(isDistrictDirtyVar);

  const handleHighlight = useCallback(
    data => () => {
      if (highlightedDistrict && data.id === highlightedDistrict.id) return;
      if (isDistrictDirty && !window.confirm(t('City.ConfirmLoseChanges'))) return;
      isDistrictDirtyVar(false);
      setHighlightedDistrict(data);
    },
    [highlightedDistrict, isDistrictDirty, t]
  );

  const handleSelect = useCallback(
    id => () => {
      const ids = selectedDistrictIdsVar();
      const selected = ids.includes(id);
      if (selected) selectedDistrictIdsVar(ids.filter(_id => _id !== id));
      else selectedDistrictIdsVar([...ids, id]);
    },
    []
  );

  const handleDistrictSaved = useCallback(
    district => {
      isDistrictDirtyVar(false);
      const wasNew = !districts.some(d => d.id === district.id);
      if (wasNew) {
        if (highlightedDistrict) {
          const highlightedId = parseId(highlightedDistrict.id);
          if (!highlightedId) setHighlightedDistrict(district);
        }
        refetch();
        const cancelAdd = cancelDistrictAddRef();
        cancelAdd && cancelAdd();
      }
    },
    [refetch, districts, highlightedDistrict]
  );

  const handleDistrictsDeleted = useCallback(
    districtIds => {
      if (districtIds.some(id => !districts.some(district => district.id === id))) return;
      const ids = selectedDistrictIdsVar();
      selectedDistrictIdsVar(ids.filter(id => !districtIds.includes(id)));
      if (districtIds.includes(highlightedDistrict.id)) setHighlightedDistrict(null);
      refetch();
    },
    [refetch, districts, highlightedDistrict]
  );

  return (
    <DistrictsCitiesCard disablePadding>
      <DistrictsCitiesContainer>
        <DistrictContainer column gap={1} p={2} shrink={0}>
          <CitiesTableComponent
            cities={districts}
            type={CityType.District}
            parentCityId={id}
            selectedIdsVar={selectedDistrictIdsVar}
            RowComponent={({ data }) => {
              const selected = selectedDistrictIds.includes(data.id);
              const isNew = isNewEntity(data);

              return (
                <DistrictRow hover inactive={data.inactive} selected={data.id === highlightedDistrict?.id} onClick={handleHighlight(data)}>
                  <TableCell width={50} onClick={event => event.stopPropagation()}>
                    <CitySelectionCheckbox checked={selected} onChange={handleSelect(data.id)} disabled={isNew || !canWrite} />
                  </TableCell>
                  <TableCell>{isNew ? t('City.NewDistrict') : data.name}</TableCell>
                </DistrictRow>
              );
            }}
            loading={loading}
            onCitiesDeleted={handleDistrictsDeleted}
            cancelAddRef={cancelDistrictAddRef}
          />
        </DistrictContainer>
        <DistrictsContainer grow p={2}>
          {!highlightedDistrict && (
            <FlexBox grow justifyContent='center' alignItems='center'>
              <Typography fontStyle='italic' color='textPrimary' variant='subtitle2'>
                {t('City.NoSelectedDistrict')}
              </Typography>
            </FlexBox>
          )}
          {highlightedDistrict && <DistrictComponent data={highlightedDistrict} onDistrictSaved={handleDistrictSaved} />}
        </DistrictsContainer>
      </DistrictsCitiesContainer>
    </DistrictsCitiesCard>
  );
};

HierarchyDetailsComponent.propTypes = {
  id: PropTypes.number
};

export default HierarchyDetailsComponent;
